import { isCSS, isJS, yieldToMain } from '../utils';
import {
  IComponentLoader,
  IResourceModel,
  IResourcePartModel,
  IBundleLoader,
  IComponentBundleStructure,
  IComponentAsset,
} from '../types';
import { createManifestAPI } from '../manifest/createManifestAPI';
import { createException, RegistryErrorCode } from '../createException';

export function getComponentAssets({
  resource,
  parts = {},
}: {
  resource?: IResourceModel;
  parts?: Record<string, IResourcePartModel>;
}) {
  const assets: Array<IComponentAsset> = [];
  const applyAssets = (_resource?: IResourceModel | IResourcePartModel) => {
    if (_resource?.src) {
      if (_resource.id && _resource.src.js) {
        assets.push({
          id: _resource.id,
          url: _resource.src.js,
          type: 'model',
        });
      }

      if (_resource.src.css) {
        assets.push({
          url: _resource.src.css,
          type: 'style',
        });
      }

      _resource.src.deps?.forEach(dep => {
        assets.push({
          url: dep,
          dependency: true,
          type: isJS(dep) ? 'model' : 'style',
        });
      });
    }
  };

  applyAssets(resource);

  Object.keys(parts).forEach(partName => {
    applyAssets(parts[partName]);
  });

  return assets;
}

export function createComponentLoader<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
>({
  resource,
  parts = {},
  loadBundle,
  statics,
}: {
  resource: IResourceModel;
  parts: Record<string, IResourcePartModel>;
  loadBundle: IBundleLoader;
  statics: Record<string, any>;
}) {
  const loader: IComponentLoader<TComponentModel, TParts> = async part => {
    /**
     * TODO:
     * - filter model & part deps
     */
    if (resource.src.deps) {
      await Promise.all([
        ...resource.src.deps.filter(isJS).map(dep =>
          loadBundle({
            url: dep,
          }),
        ),
        loadBundle({
          assets: resource.src.deps.filter(isCSS),
        }),
      ]);
    }

    const tasks: Array<
      Promise<IComponentBundleStructure<TComponentModel> | null>
    > = [];

    if (part) {
      const componentPart = parts[part as string];

      tasks.push(
        loadBundle({
          id: componentPart.id,
          url: componentPart.src.js,
          assets: [componentPart.src.css],
        }),
      );
    } else if (resource.id) {
      tasks.push(
        loadBundle({
          id: resource.id,
          url: resource.src.js,
          assets: [resource.src.css],
        }),
      );

      const batchRequests = Object.keys(parts)
        /**
         * get eager parts
         */
        .filter(partName => parts[partName].batched);

      batchRequests.forEach(partName => {
        const batchedPart = parts[partName];

        tasks.push(
          loadBundle({
            id: batchedPart.id,
            url: batchedPart.src.js,
            assets: [batchedPart.src.css],
          }),
        );
      });
    }

    const bundles = await Promise.all(tasks);

    if (bundles.some(bundle => !bundle || !bundle.components)) {
      throw createException(
        RegistryErrorCode.NoComponentsAtComponentBundle,
        '',
        {
          missingBundle: resource.name,
        },
      );
    }

    const model = Object.assign(
      {},
      ...bundles.map((bundle: any) =>
        bundle ? bundle.components[resource.name] : {},
      ),
    );

    return part ? model[part] : model;
  };

  loader.isPartExist = part => part in parts;
  loader.statics = statics;
  loader.assets = getComponentAssets({ resource, parts });

  return loader;
}

export async function createLazyComponentLoaders<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
>({
  manifest,
  loadBundle,
}: {
  manifest: ReturnType<typeof createManifestAPI>;
  loadBundle: IBundleLoader;
}) {
  const components = manifest.getComponents();
  // Break down the long task to smaller tasks
  await yieldToMain();

  const parts = manifest.getParts();
  const loaders: Record<string, IComponentLoader<TComponentModel, TParts>> = {};

  Object.keys(components).forEach(componentName => {
    loaders[componentName] = createComponentLoader<TComponentModel, TParts>({
      resource: components[componentName],
      parts: parts[componentName],
      loadBundle,
      statics: manifest.getStatics(componentName),
    });
  });

  return loaders;
}

export async function createEagerComponentLoaders<TComponentModel>({
  manifest,
  loadBundle,
}: {
  manifest: ReturnType<typeof createManifestAPI>;
  loadBundle: IBundleLoader;
}) {
  const components = manifest.getComponents();
  const parts = manifest.getParts();

  const resource = manifest.getHostBundleModel();

  if (!resource.src.js || !resource.id) {
    return {};
  }

  const bundle = await loadBundle<IComponentBundleStructure<TComponentModel>>({
    id: resource.id,
    url: resource.src.js,
    assets: [resource.src.css],
  });

  if (!bundle || !bundle.components) {
    throw createException(RegistryErrorCode.NoComponentsAtHostBundle);
  }

  const loaders: Record<string, IComponentLoader<TComponentModel, never>> = {};

  Object.keys(bundle.components).forEach(componentName => {
    const loader = (() =>
      Promise.resolve(bundle.components[componentName])) as IComponentLoader<
      TComponentModel,
      never
    >;
    loader.assets = getComponentAssets({
      resource: components[componentName],
      parts: parts[componentName],
    });
    loader.statics = manifest.getStatics(componentName);
    loader.isPartExist = () => true;

    loaders[componentName] = loader;
  });

  return loaders;
}
